"use client";

import Image from "next/image";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowUpRight } from "lucide-react";

import type { FC } from "react";

import { LEAD_DROP_VARIANTS } from "@/constants/leadConstants";
import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitle } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";

type Lfl1003Profile = {
  id?: number | string;
  desktop_image?: string;
  mobile_image?: string;
  image_alt?: string;
  profile_name?: string;
  profile_title?: string;
  logo_1?: string;
  logo_1_alt?: string;
  logo_2?: string;
  logo_2_alt?: string;
  priority?: number | string;
  is_active?: boolean | number;
};

interface LearnFromLeaders1003Data {
  id?: number | string;
  /** MySQL booleans may arrive as 0/1 */
  status?: boolean | number;
  title?: string;
  title_start_color?: string;
  title_end_color?: string;
  description?: string;
  description_color?: string;
  background_image?: string;
  background_image_alt?: string;
  /** Lead CTA under the description — empty text hides the button */
  cta_text?: string;
  cta_color?: string;
  cta_text_color?: string;
  profiles?: Lfl1003Profile[];
  metadata?: Lfl1003Profile[];
}

interface LearnFromLeaders1003Props {
  /** Flat row from API, or `{ data: row }` from some admin previews */
  data: LearnFromLeaders1003Data | { data: LearnFromLeaders1003Data };
}

function resolvePayload(
  data: LearnFromLeaders1003Props["data"]
): LearnFromLeaders1003Data | undefined {
  if (!data || typeof data !== "object") return undefined;
  const anyData = data as Record<string, unknown>;
  if ("data" in anyData && anyData.data != null && typeof anyData.data === "object") {
    return anyData.data as LearnFromLeaders1003Data;
  }
  return data as LearnFromLeaders1003Data;
}

const LeaderCard1003: FC<{ profile: Lfl1003Profile }> = ({ profile }) => {
  const logos = [
    { src: profile.logo_1, alt: profile.logo_1_alt },
    { src: profile.logo_2, alt: profile.logo_2_alt },
  ].filter((logo) => (logo.src || "").trim());

  return (
    <div className="h-[332px] min-w-[276px] max-w-[280px] shrink-0 shadow-[0px_4px_17.4px_0px_#0000002E]">
      <div className="relative flex h-[156px] items-center justify-center bg-[#E1473F]">
        {profile.desktop_image && (
          <Image
            src={profile.desktop_image}
            alt={profile.image_alt || profile.profile_name || ""}
            fill
            className="object-cover"
            sizes="280px"
          />
        )}
      </div>
      <div className="flex flex-col gap-3.5 bg-[#FFFFFF] px-[18px] py-4">
        <div className="flex flex-col gap-1.5 text-center">
          <p className="text-[16px] font-bold leading-[1.5] text-[#171719] min-[768px]:font-semibold min-[768px]:leading-normal">
            {profile.profile_name || ""}
          </p>
          {profile.profile_title && (
            <p className="text-[14px] font-normal leading-[1.5] text-[#171719]">
              {profile.profile_title}
            </p>
          )}
        </div>
        {logos.length > 0 && <div className="h-px w-full bg-[#FFEFEE]" />}
        <div className="flex h-[60px] justify-center gap-4 overflow-hidden">
          {logos.map((logo, index) => (
            <React.Fragment key={index}>
              {index > 0 && <div className="w-px self-stretch bg-[#FFEFEE]" />}
              <div className="relative h-[60px] w-[114px]">
                <Image
                  src={logo.src as string}
                  alt={logo.alt || ""}
                  fill
                  className="object-contain"
                  sizes="114px"
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

const LearnFromLeaders1003 = ({ data }: LearnFromLeaders1003Props) => {
  const section = resolvePayload(data);
  const elementRef = useRef<HTMLElement>(null);
  const dispatch = useDispatch();
  const [isCtaHovered, setIsCtaHovered] = useState(false);

  useEffect(() => {
    if (elementRef.current) {
      FireWidgetViewedEvent(elementRef, "learn_from_leaders_section");
    }
  }, []);

  const rawProfiles = section?.profiles;
  const rawMetadata = section?.metadata;
  const profiles: Lfl1003Profile[] =
    Array.isArray(rawProfiles) && rawProfiles.length > 0
      ? rawProfiles
      : Array.isArray(rawMetadata)
        ? rawMetadata
        : [];

  const activeProfiles = useMemo(() => {
    return profiles
      .filter((p) => p.is_active !== false && p.is_active !== 0)
      .sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0));
  }, [profiles]);

  if (!section) return null;
  if (section.status === false || section.status === 0) return null;
  if (activeProfiles.length === 0) return null;

  const {
    title = "",
    title_start_color,
    title_end_color,
    description = "",
    description_color,
    background_image,
    background_image_alt,
    cta_text,
    cta_color,
    cta_text_color,
  } = section;

  const ctaText = (cta_text || "").trim();

  return (
    <section
      ref={elementRef}
      aria-label="Learn from leaders"
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        /* `w-full` like every sibling section — without it the section shrinks to
           its content and the page background shows as grey gaps left and right. */
        "relative mx-auto flex w-full items-center justify-center overflow-hidden",
        "bg-[#FFFFFF] px-7 pb-16 pt-11 min-[768px]:py-10"
      )}
      style={
        {
          fontFamily: "Poppins, sans-serif",
          "--lfl-accent": title_end_color || "#E1473F",
        } as React.CSSProperties
      }
      data-widget="learn-from-leaders-1003"
    >
      {/* Background image */}
      {background_image && (
        <Image
          src={background_image}
          alt={background_image_alt || ""}
          fill
          className="-z-10 object-cover object-center"
          sizes="100vw"
        />
      )}

      {/* CU's `w-screen` token is overridden to 1114px — translate it literally, not as 100vw */}
      <div className="relative z-10 flex w-full flex-col items-center justify-center min-[1024px]:w-[1114px]">
        <div className="relative flex w-full flex-col items-center justify-center gap-[30px] py-2.5">
          {/* Title + Description */}
          <div className="flex w-[320px] flex-col items-center justify-center gap-3.5 text-center min-[768px]:w-[766px] min-[768px]:gap-1.5">
            <h2
              className="text-[28px] font-semibold leading-normal min-[768px]:text-[32px] [&_span]:whitespace-normal [&_span]:text-[color:var(--lfl-accent)]"
              style={{ color: title_start_color || "#171719" }}
              dangerouslySetInnerHTML={{
                __html: formatSectionTitle(title, ""),
              }}
            />
            {description && (
              <p
                className="text-[14px] font-normal leading-[1.5] min-[768px]:text-[18px]"
                style={{ color: description_color || "#171719" }}
              >
                {description}
              </p>
            )}

            {ctaText && (
              <button
                type="button"
                className="mt-2 flex cursor-pointer items-center justify-center gap-2.5 rounded-md px-[18px] py-3 text-[16px] font-semibold leading-normal min-[768px]:mt-3"
                style={{
                  backgroundColor: cta_color || "#E1473F",
                  color: cta_text_color || "#FFFFFF",
                }}
                onClick={() =>
                  dispatch(
                    handleOpenLeaddrop({
                      variant: LEAD_DROP_VARIANTS.REGISTER,
                    })
                  )
                }
                onMouseEnter={() => setIsCtaHovered(true)}
                onMouseLeave={() => setIsCtaHovered(false)}
              >
                {ctaText}
                <span className="size-6">
                  <ArrowUpRight
                    className={cn(
                      "transition-transform duration-300",
                      isCtaHovered ? "rotate-[45deg]" : "rotate-0"
                    )}
                  />
                </span>
              </button>
            )}
          </div>

          {/* Leaders cards */}
          <div
            className={cn(
              "flex flex-col items-center justify-center gap-[38px] overflow-auto",
              "min-[768px]:w-full min-[768px]:flex-row min-[768px]:justify-start min-[768px]:px-16"
            )}
          >
            {activeProfiles.map((profile, index) => (
              <LeaderCard1003 key={profile.id ?? index} profile={profile} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearnFromLeaders1003;
